// Atomic Mode — convert a region of the 2D material sandbox into a LAMMPS
// molecular-dynamics system (data file + input script).
//
// IMPORTANT — license boundary: this module is ours (MIT OR Apache-2.0). It
// must NOT import anything from `vendor/atomify` (GPL-3.0). It only produces
// plain LAMMPS text; the host (`atomicMode.ts`) hands that to atomify over a
// postMessage channel, so atomify stays at arm's length in an iframe without
// its copyleft reaching our codebase.
//
// Pipeline:
//   grid snapshot (material ids + temperature) over a selected hotspot
//     -> per-material crystal lattice placement of atoms (buildLammpsSystem)
//     -> LAMMPS data file + input script (writeDataFile / writeInputScript)
//   The host then pushes those files into the atomify iframe via postMessage.

import type { MaterialDef } from "./types";
import type { ElementInfo } from "./elements";
import {
  cellAtomCount,
  fillLatticeCells,
  type CrystalStructure,
} from "./crystal";
import { fallbackComposition, type MaterialLike } from "./fallbackComposition";
import { measurementVariableLines } from "./measurements";
import { hasUffParams, uffForElement } from "./uff";

/** A rectangular hotspot in grid coordinates, inclusive of both ends. */
export interface GridRegion {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

/** Everything the converter needs read off the paused sandbox. */
export interface GridSnapshot {
  width: number;
  height: number;
  /** Material id per cell, row-major: `materials[y * width + x]`. */
  materials: Uint16Array;
  /** Temperature in Kelvin per cell, same indexing. */
  temperature: Float32Array;
}

/** Static inputs that don't change per snapshot. */
export interface ConverterContext {
  /** Material table indexed by material id (index === id). */
  materials: MaterialDef[];
  /** Element symbol -> {z, atomicWeight}. */
  elements: Map<string, ElementInfo>;
  /**
   * Real-world width of one sandbox cell, in ångström. The grid is
   * unitless, so this sets the physical scale of the produced system.
   * Smaller -> denser, cheaper LAMMPS runs.
   */
  cellAngstrom?: number;
  /**
   * Soft ceiling on the total number of atoms. When the selection has more
   * cells than this allows, cells are sampled on a stride so the run stays
   * light. Defaults to DEFAULT_ATOM_BUDGET.
   */
  atomBudget?: number;
  /**
   * Optional element ordering for atom types. When set, atom types are
   * assigned to match this order (1-based), so a recipe's `pair_coeff`
   * element list lines up with the data-file types. Elements not listed get
   * appended after. When unset, types are assigned in first-seen order.
   */
  forceTypeOrder?: string[];
  /**
   * Optional real crystal structure. When set, every occupied region cell is
   * filled with this lattice (geometrically exact) instead of the generic
   * cubic grid. The lattice's basis elements map onto the dominant material's
   * composition elements in order.
   */
  crystal?: {
    structure: CrystalStructure;
    /** Lattice constant a (Å). */
    a: number;
    /** Optional c (Å) for hexagonal structures. */
    c?: number;
  };
}

/** One placed atom in the final LAMMPS system. */
export interface Atom {
  /** 1-based LAMMPS atom type. */
  type: number;
  x: number;
  y: number;
  z: number;
  /** Source cell temperature (K) — seeds the initial velocity. */
  tempK: number;
}

/** A distinct LAMMPS atom type: one chemical element. */
export interface AtomType {
  /** 1-based id used in the data file. */
  id: number;
  symbol: string;
  z: number;
  mass: number;
  /** UFF Lennard-Jones well depth ε in kcal/mol (LAMMPS `units real`). */
  epsilon: number;
  /** UFF Lennard-Jones σ in Å. */
  sigma: number;
  /** True when this came from genuine UFF data (false = super-heavy fallback). */
  uffKnown: boolean;
}

export interface LammpsSystem {
  atoms: Atom[];
  types: AtomType[];
  /** Simulation box in ångström. */
  box: { lo: [number, number, number]; hi: [number, number, number] };
  /** Mean temperature (K) across placed atoms — the run's thermostat target. */
  meanTempK: number;
  /** Distinct material ids present in the selection (for recipe selection). */
  materialIds: string[];
  /** Distinct element symbols present, in atom-type order. */
  elementSymbols: string[];
}

const DEFAULT_CELL_ANGSTROM = 6;
// How many cells deep the extruded 3D slab is. The sandbox is 2D, but the
// LAMMPS run is full 3D: each occupied cell is extruded along z into a small
// block, so the result is a chunky 3D crystal rather than a flat film.
const SLAB_THICKNESS_FACTOR = 2;

// Atom-count controls. The run should stay light enough to be interactive in
// the browser, so each occupied cell contributes only a few atoms (a tiny
// representative block, not a dense lattice), and the whole selection is held
// under a global budget by striding over cells when it's large.
const MAX_ATOMS_PER_AXIS = 2; // in-plane: up to 2×2 atoms per cell
const MAX_DEPTH_LAYERS = 2; // up to 2 atoms deep per cell
// Target ceiling on total atoms. Kept low so the in-browser WASM LAMMPS loads
// and runs quickly; the host overrides it from VITE_ATOMIC_MAX_ATOMS so larger
// machines can raise it. The cubic path strides over cells (preserving each
// material's spatial ratio) and the global formula cursor preserves
// stoichiometry, so downsampling keeps every material's proportion.
const DEFAULT_ATOM_BUDGET = 150;

/**
 * Estimate a cubic-lattice spacing (ångström) for a material from its mass
 * density and mean atomic mass: a = (m_atom / rho)^(1/3). Falls back to a
 * sensible solid-ish spacing when density/mass are unknown so every material
 * still produces atoms.
 */
export function latticeSpacingAngstrom(
  densityKgM3: number | undefined,
  meanAtomicMass: number,
): number {
  // amu -> kg, then number density n = rho / m_atom (atoms / m^3),
  // spacing a = n^(-1/3), converted m -> ångström (1 m = 1e10 Å).
  const AMU_KG = 1.66053907e-27;
  if (densityKgM3 && densityKgM3 > 0 && meanAtomicMass > 0) {
    const mAtom = meanAtomicMass * AMU_KG;
    const n = densityKgM3 / mAtom; // atoms per m^3
    const aMeters = Math.cbrt(1 / n);
    const aAng = aMeters * 1e10;
    // Clamp to a physically plausible range to avoid pathological packing
    // from bad data (e.g. a gas density giving a 30 Å spacing inside a 6 Å
    // cell would emit zero atoms).
    return Math.min(Math.max(aAng, 1.5), 5);
  }
  return 2.8; // ~iron-ish default
}

/** A material's composition: its explicit one, or a representative fallback so
 * every material deconstructs into atoms. Never empty. */
function effectiveComposition(
  mat: MaterialDef & { composition?: { id: string; n: number }[] },
): { id: string; n: number }[] {
  if (mat.composition && mat.composition.length > 0) return mat.composition;
  return fallbackComposition(mat as MaterialLike);
}

/** One element of a resolved composition, with its element data and count. */
interface ResolvedSpecies {
  symbol: string;
  info: ElementInfo;
  n: number;
}

/**
 * Resolve a material to the list of its composition elements that we have
 * element data for, with their stoichiometric counts. Empty for empty/unknown
 * materials. This is what lets a cell be built from its *real* atoms (e.g.
 * water -> [H×2, O×1], salt -> [Na×1, Cl×1]) rather than a single element.
 */
function resolvedComposition(
  ctx: ConverterContext,
  materialId: number,
): ResolvedSpecies[] {
  const mat = ctx.materials[materialId];
  if (!mat || mat.id === "empty") return [];
  const out: ResolvedSpecies[] = [];
  for (const ref of effectiveComposition(mat)) {
    const info = ctx.elements.get(ref.id);
    if (info && ref.n > 0) out.push({ symbol: ref.id, info, n: ref.n });
  }
  return out;
}

/** Mean temperature (K) over occupied-or-not cells in a region rectangle. */
function avgTemp(
  snap: GridSnapshot,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
): number {
  let sum = 0;
  let n = 0;
  for (let gy = y0; gy <= y1; gy++) {
    for (let gx = x0; gx <= x1; gx++) {
      sum += snap.temperature[gy * snap.width + gx] || 293.15;
      n++;
    }
  }
  return n > 0 ? sum / n : 293.15;
}

/** Greatest common divisor, for reducing a formula to its simplest ratio. */
function gcd(a: number, b: number): number {
  return b === 0 ? a : gcd(b, a % b);
}

/**
 * Expand a composition into one minimal formula unit as an interleaved element
 * sequence, e.g. water [H×2, O×1] -> [H, O, H], salt [Na, Cl] -> [Na, Cl].
 * Counts are reduced by their GCD first so the unit is as small as possible
 * (so even tiny per-cell blocks reflect the ratio). Interleaving (rather than
 * H,H,O) keeps the species well-mixed in space.
 *
 * A per-material iterator cycles through this unit across every atom slot in
 * every cell, so the *global* stoichiometry of the produced system is exactly
 * the material's formula regardless of how many atoms each cell holds.
 */
function formulaUnit(comp: ResolvedSpecies[]): ResolvedSpecies[] {
  if (comp.length === 0) return [];
  const counts = comp.map((c) => Math.max(1, Math.round(c.n)));
  const divisor = counts.reduce((g, n) => gcd(g, n));
  const reduced = comp.map((c, i) => ({
    species: c,
    remaining: counts[i] / divisor,
  }));
  const unit: ResolvedSpecies[] = [];
  let progress = true;
  while (progress) {
    progress = false;
    for (const r of reduced) {
      if (r.remaining > 0) {
        unit.push(r.species);
        r.remaining--;
        progress = true;
      }
    }
  }
  return unit;
}

/**
 * Place atoms for the selected region using a per-material cubic crystal
 * lattice. Each occupied 2D cell is extruded into a solid 3D cube of atoms
 * (x, y and z) whose spacing comes from the material's density, typed by the
 * material's primary element. This is the "per-material crystal lattice"
 * mapping: dense metals pack tightly, light materials loosely, all within the
 * same physical box. The result is a full 3D crystal slab, not a flat plane.
 */
export function buildLammpsSystem(
  snap: GridSnapshot,
  region: GridRegion,
  ctx: ConverterContext,
): LammpsSystem {
  const cellAng = ctx.cellAngstrom ?? DEFAULT_CELL_ANGSTROM;
  const x0 = Math.max(0, Math.min(region.x0, region.x1));
  const x1 = Math.min(snap.width - 1, Math.max(region.x0, region.x1));
  const y0 = Math.max(0, Math.min(region.y0, region.y1));
  const y1 = Math.min(snap.height - 1, Math.max(region.y0, region.y1));

  // Keep the run light. Sample cells on a stride so roughly `budget` cells
  // contribute — striding uniformly preserves each material's spatial ratio.
  // We deliberately sample MANY cells (≈budget, ~1 atom each) rather than few
  // dense cells: this gives every material in the selection a fair chance to be
  // represented (minority materials aren't aliased out), and the final
  // atom-stride downsample below trims the result to `budget`.
  const budget = ctx.atomBudget ?? DEFAULT_ATOM_BUDGET;
  const cellsWide = x1 - x0 + 1;
  const cellsHigh = y1 - y0 + 1;
  const totalCells = cellsWide * cellsHigh;
  const stride = Math.max(
    1,
    Math.ceil(Math.sqrt(totalCells / Math.max(1, budget))),
  );

  // Assign a 1-based atom type per distinct element symbol. When a recipe
  // forces an element order, pre-seed the type ids so the data file's types
  // line up with the recipe's `pair_coeff` element list.
  const typeBySymbol = new Map<string, AtomType>();
  const makeType = (
    symbol: string,
    info: ElementInfo | undefined,
  ): AtomType => {
    const uff = uffForElement(symbol);
    const elInfo = info ?? ctx.elements.get(symbol);
    return {
      id: typeBySymbol.size + 1,
      symbol,
      z: elInfo?.z ?? 0,
      mass: elInfo?.atomicWeight ?? 0,
      epsilon: uff.epsilon,
      sigma: uff.sigma,
      uffKnown: hasUffParams(symbol),
    };
  };
  for (const sym of ctx.forceTypeOrder ?? []) {
    if (!typeBySymbol.has(sym)) typeBySymbol.set(sym, makeType(sym, undefined));
  }
  const ensureType = (symbol: string, info: ElementInfo): number => {
    const existing = typeBySymbol.get(symbol);
    if (existing) return existing.id;
    const t = makeType(symbol, info);
    typeBySymbol.set(symbol, t);
    return t.id;
  };

  const atoms: Atom[] = [];
  const materialIdSet = new Set<string>();
  let tempSum = 0;

  // ---- Crystal-lattice path -------------------------------------------------
  // When a real crystal structure is requested, fill the region's footprint
  // with that lattice (geometrically exact) rather than the per-cell grid. The
  // lattice's basis elements map onto the dominant material's composition.
  if (ctx.crystal) {
    // Pick the dominant (most common) occupied material in the region.
    const counts = new Map<number, number>();
    for (let gy = y0; gy <= y1; gy++) {
      for (let gx = x0; gx <= x1; gx++) {
        const m = snap.materials[gy * snap.width + gx];
        const comp = resolvedComposition(ctx, m);
        if (comp.length > 0) counts.set(m, (counts.get(m) ?? 0) + 1);
      }
    }
    if (counts.size > 0) {
      const dominantId = [...counts.entries()].sort(
        (a, b) => b[1] - a[1],
      )[0][0];
      const matDef = ctx.materials[dominantId];
      if (matDef) materialIdSet.add(matDef.id);
      const comp = resolvedComposition(ctx, dominantId);
      // Order the composition by the forced type order if given (recipe).
      const orderedComp = ctx.forceTypeOrder
        ? ctx.forceTypeOrder
            .map((sym) => comp.find((c) => c.symbol === sym))
            .filter((c): c is ResolvedSpecies => !!c)
        : comp;
      const tempK = avgTemp(snap, x0, y0, x1, y1);

      // Size the periodic crystal to the atom budget: pick whole-cell counts so
      // the total atom count is near `budget`. A 2-cell-thick slab keeps the
      // 3D look; the rest is split between x and y. The crystal is periodic, so
      // a small box is fully representative.
      const atomsPerCell = cellAtomCount(ctx.crystal.structure);
      const cellsNeeded = Math.max(1, budget / atomsPerCell);
      const nz = cellsNeeded >= 8 ? 2 : 1;
      const nxy = Math.max(1, Math.round(Math.sqrt(cellsNeeded / nz)));

      const fill = fillLatticeCells(
        ctx.crystal.structure,
        ctx.crystal.a,
        nxy,
        nxy,
        nz,
        ctx.crystal.c,
      );
      // Shift atoms so the lowest coordinate sits at 0 (hexagonal cells have
      // negative x), keeping every atom inside [0, box).
      let minX = Infinity;
      let minY = Infinity;
      let minZ = Infinity;
      for (const la of fill.atoms) {
        if (la.x < minX) minX = la.x;
        if (la.y < minY) minY = la.y;
        if (la.z < minZ) minZ = la.z;
      }
      for (const la of fill.atoms) {
        const species = orderedComp[la.element % orderedComp.length];
        if (!species) continue;
        const typeId = ensureType(species.symbol, species.info);
        atoms.push({
          type: typeId,
          x: la.x - minX,
          y: la.y - minY,
          z: la.z - minZ,
          tempK,
        });
        tempSum += tempK;
      }

      const types = [...typeBySymbol.values()].sort((a, b) => a.id - b.id);
      return {
        atoms,
        types,
        box: { lo: [0, 0, 0], hi: [fill.box.x, fill.box.y, fill.box.z] },
        meanTempK: atoms.length > 0 ? tempSum / atoms.length : 293.15,
        materialIds: [...materialIdSet],
        elementSymbols: types.map((t) => t.symbol),
      };
    }
  }

  // ---- Generic cubic-grid path ----------------------------------------------
  const depthAng = cellAng * SLAB_THICKNESS_FACTOR;

  // Pass 1: collect the occupied sampled cells. Each sampled cell occupies one
  // `cellAng`-wide slot in the output (strided index), so they stay contiguous.
  interface OccCell {
    outX: number;
    outY: number;
    materialId: number;
    tempK: number;
  }
  const occ: OccCell[] = [];
  let outY = 0;
  for (let gy = y0; gy <= y1; gy += stride, outY++) {
    let outX = 0;
    for (let gx = x0; gx <= x1; gx += stride, outX++) {
      const idx = gy * snap.width + gx;
      const materialId = snap.materials[idx];
      if (resolvedComposition(ctx, materialId).length === 0) continue;
      const matDef = ctx.materials[materialId];
      if (matDef) materialIdSet.add(matDef.id);
      occ.push({
        outX,
        outY,
        materialId,
        tempK: snap.temperature[idx] || 293.15,
      });
    }
  }

  // Per-cell atom count chosen so the total lands near the budget. The
  // per-material formula cursor (below) makes the ratio exact regardless of how
  // many atoms each cell gets, so even 1 atom/cell preserves stoichiometry; the
  // count just controls visual density. If even one atom per occupied cell
  // would exceed the budget, uniformly subsample the cell list first (this
  // preserves each material's spatial ratio since occ is region-ordered).
  let cells = occ;
  if (cells.length > budget && budget > 0) {
    const s = Math.ceil(cells.length / budget);
    cells = cells.filter((_, i) => i % s === 0);
  }
  // floor (not round) so the total never exceeds the budget.
  const atomsPerCell = Math.max(
    1,
    Math.min(
      MAX_ATOMS_PER_AXIS * MAX_ATOMS_PER_AXIS * MAX_DEPTH_LAYERS,
      Math.floor(budget / Math.max(1, cells.length)),
    ),
  );
  // Arrange the per-cell atoms in a compact sub-grid (≤2 deep keeps the 3D look).
  const perDepth = atomsPerCell >= 4 ? Math.min(MAX_DEPTH_LAYERS, 2) : 1;
  const perAxis = Math.max(1, Math.ceil(Math.sqrt(atomsPerCell / perDepth)));
  const step = cellAng / perAxis;
  const depthStep = depthAng / perDepth;

  // Per-material formula unit + a running cursor into it, so atoms are drawn in
  // the material's exact stoichiometric ratio across all of its cells (the
  // cursor persists between cells of the same material → the global ratio is
  // exact, e.g. water is exactly 2:1 H:O system-wide).
  const formulaByMaterial = new Map<number, ResolvedSpecies[]>();
  const cursorByMaterial = new Map<number, number>();

  // Pass 2: place exactly `atomsPerCell` atoms per occupied cell.
  for (const cell of cells) {
    let unit = formulaByMaterial.get(cell.materialId);
    if (!unit) {
      unit = formulaUnit(resolvedComposition(ctx, cell.materialId));
      formulaByMaterial.set(cell.materialId, unit);
    }
    const ox = cell.outX * cellAng;
    const oy = cell.outY * cellAng;
    let cursor = cursorByMaterial.get(cell.materialId) ?? 0;
    let placed = 0;
    for (let i = 0; i < perAxis && placed < atomsPerCell; i++) {
      for (let j = 0; j < perAxis && placed < atomsPerCell; j++) {
        for (let k = 0; k < perDepth && placed < atomsPerCell; k++) {
          const species = unit[cursor % unit.length];
          cursor++;
          placed++;
          const typeId = ensureType(species.symbol, species.info);
          atoms.push({
            type: typeId,
            x: ox + (i + 0.5) * step,
            y: oy + (j + 0.5) * step,
            z: -0.5 * depthAng + (k + 0.5) * depthStep,
            tempK: cell.tempK,
          });
          tempSum += cell.tempK;
        }
      }
    }
    cursorByMaterial.set(cell.materialId, cursor);
  }

  // Output box spans the number of sampled cells, not the original selection.
  const sampledWide = Math.ceil(cellsWide / stride);
  const sampledHigh = Math.ceil(cellsHigh / stride);
  const spanX = sampledWide * cellAng;
  const spanY = sampledHigh * cellAng;
  // A symmetric z-slab around 0, SLAB_THICKNESS_FACTOR cells deep.
  const halfZ = 0.5 * cellAng * SLAB_THICKNESS_FACTOR;

  const types = [...typeBySymbol.values()].sort((a, b) => a.id - b.id);
  return {
    atoms,
    types,
    box: { lo: [0, 0, -halfZ], hi: [spanX, spanY, halfZ] },
    meanTempK: atoms.length > 0 ? tempSum / atoms.length : 293.15,
    materialIds: [...materialIdSet],
    elementSymbols: types.map((t) => t.symbol),
  };
}

/**
 * Build a HYBRID system for a mixed selection containing carbon: the carbon is
 * placed as a real graphene honeycomb sheet (so AIREBO bonds it into genuine
 * sp² carbon) and the other elements are placed as a Lennard-Jones cluster just
 * above the sheet. Returned with `carbonType` so the caller can write a
 * `pair_style hybrid airebo lj/cut` script (AIREBO for C–C, LJ for the rest).
 * Returns null if there's no carbon, or no non-carbon atoms (use normal paths).
 */
export function buildHybridCarbonSystem(
  snap: GridSnapshot,
  region: GridRegion,
  ctx: ConverterContext,
): (LammpsSystem & { carbonType: number }) | null {
  const budget = ctx.atomBudget ?? DEFAULT_ATOM_BUDGET;
  const x0 = Math.max(0, Math.min(region.x0, region.x1));
  const x1 = Math.min(snap.width - 1, Math.max(region.x0, region.x1));
  const y0 = Math.max(0, Math.min(region.y0, region.y1));
  const y1 = Math.min(snap.height - 1, Math.max(region.y0, region.y1));

  // Classify occupied cells: carbon (primary element C) vs other.
  let carbonCells = 0;
  const otherUnits: ResolvedSpecies[][] = []; // formula unit per non-carbon cell
  const materialIdSet = new Set<string>();
  let tempSum = 0;
  let tempN = 0;
  for (let gy = y0; gy <= y1; gy++) {
    for (let gx = x0; gx <= x1; gx++) {
      const m = snap.materials[gy * snap.width + gx];
      const comp = resolvedComposition(ctx, m);
      if (comp.length === 0) continue;
      const matDef = ctx.materials[m];
      if (matDef) materialIdSet.add(matDef.id);
      tempSum += snap.temperature[gy * snap.width + gx] || 293.15;
      tempN++;
      if (comp[0].symbol === "C") carbonCells++;
      else otherUnits.push(formulaUnit(comp));
    }
  }
  if (carbonCells === 0 || otherUnits.length === 0) return null;

  const tempK = tempN > 0 ? tempSum / tempN : 293.15;
  const total = carbonCells + otherUnits.length;
  // Split the budget by cell share, with at least a little of each.
  const carbonBudget = Math.max(8, Math.round((budget * carbonCells) / total));
  const otherBudget = Math.max(4, budget - carbonBudget);

  // Atom type registry (carbon is type 1).
  const typeBySymbol = new Map<string, AtomType>();
  const ensureType = (symbol: string, info?: ElementInfo): number => {
    const existing = typeBySymbol.get(symbol);
    if (existing) return existing.id;
    const uff = uffForElement(symbol);
    const el = info ?? ctx.elements.get(symbol);
    const t: AtomType = {
      id: typeBySymbol.size + 1,
      symbol,
      z: el?.z ?? 0,
      mass: el?.atomicWeight ?? 0,
      epsilon: uff.epsilon,
      sigma: uff.sigma,
      uffKnown: hasUffParams(symbol),
    };
    typeBySymbol.set(symbol, t);
    return t.id;
  };
  const carbonType = ensureType("C");

  const atoms: Atom[] = [];

  // --- Carbon graphene sheet (honeycomb, ~2 atoms / cell) ---
  const sheetCells = Math.max(1, Math.round(carbonBudget / 2));
  const nxy = Math.max(1, Math.round(Math.sqrt(sheetCells)));
  const sheet = fillLatticeCells("graphene", 2.46, nxy, nxy, 1);
  let minX = Infinity;
  let minY = Infinity;
  for (const a of sheet.atoms) {
    if (a.x < minX) minX = a.x;
    if (a.y < minY) minY = a.y;
  }
  for (const a of sheet.atoms) {
    atoms.push({ type: carbonType, x: a.x - minX, y: a.y - minY, z: 0, tempK });
  }
  const spanX = sheet.box.x;
  const spanY = sheet.box.y;

  // --- Other elements: LJ cluster above the sheet ---
  // Merge the non-carbon formula units (round-robin) so each material's ratio
  // is preserved, then lay them on a coarse cubic grid above the graphene.
  const merged: ResolvedSpecies[] = [];
  let more = true;
  for (let i = 0; more; i++) {
    more = false;
    for (const unit of otherUnits) {
      if (i < unit.length) {
        merged.push(unit[i]);
        more = true;
      }
    }
  }
  if (merged.length > 0) {
    const ljSpacing = 3.6; // Å between LJ atoms
    const perRow = Math.max(1, Math.floor(spanX / ljSpacing));
    const perCol = Math.max(1, Math.floor(spanY / ljSpacing));
    const gap = 3.5; // Å above the sheet
    let placed = 0;
    let cursor = 0;
    outer: for (let layer = 0; ; layer++) {
      for (let r = 0; r < perRow; r++) {
        for (let c = 0; c < perCol; c++) {
          if (placed >= otherBudget) break outer;
          const species = merged[cursor % merged.length];
          cursor++;
          placed++;
          const typeId = ensureType(species.symbol, species.info);
          atoms.push({
            type: typeId,
            x: (r + 0.5) * ljSpacing,
            y: (c + 0.5) * ljSpacing,
            z: gap + layer * ljSpacing,
            tempK,
          });
        }
      }
    }
  }

  const types = [...typeBySymbol.values()].sort((a, b) => a.id - b.id);
  // z box: graphene at 0, cluster above; pad generously, periodic in plane.
  const maxZ = atoms.reduce((m, a) => Math.max(m, a.z), 0) + 6;
  return {
    atoms,
    types,
    carbonType,
    box: { lo: [0, 0, -6], hi: [spanX, spanY, maxZ] },
    meanTempK: tempK,
    materialIds: [...materialIdSet],
    elementSymbols: types.map((t) => t.symbol),
  };
}

/**
 * Render a LAMMPS data file for the system: box, masses per type, and one line
 * per atom. The Atoms section format must match the run's `atom_style`:
 *   - "atomic": `id type x y z`
 *   - "charge": `id type q x y z`  (ReaxFF, ionic models)
 * `charges` maps element symbol -> formal charge (used for atom_style charge;
 * defaults to 0, which ReaxFF re-equilibrates via its qeq fix anyway).
 */
export function writeDataFile(
  system: LammpsSystem,
  atomStyle: "atomic" | "charge" = "atomic",
  charges?: Map<string, number>,
): string {
  const symbolByType = new Map(system.types.map((t) => [t.id, t.symbol]));
  const L: string[] = [];
  L.push("# Atomify hotspot — generated by RealChem Atomic Mode");
  L.push("");
  L.push(`${system.atoms.length} atoms`);
  L.push(`${system.types.length} atom types`);
  L.push("");
  L.push(
    `${system.box.lo[0].toFixed(4)} ${system.box.hi[0].toFixed(4)} xlo xhi`,
  );
  L.push(
    `${system.box.lo[1].toFixed(4)} ${system.box.hi[1].toFixed(4)} ylo yhi`,
  );
  L.push(
    `${system.box.lo[2].toFixed(4)} ${system.box.hi[2].toFixed(4)} zlo zhi`,
  );
  L.push("");
  L.push("Masses");
  L.push("");
  for (const t of system.types) {
    L.push(`${t.id} ${t.mass.toFixed(4)} # ${t.symbol}`);
  }
  L.push("");
  L.push(`Atoms # ${atomStyle}`);
  L.push("");
  system.atoms.forEach((a, i) => {
    const coords = `${a.x.toFixed(4)} ${a.y.toFixed(4)} ${a.z.toFixed(4)}`;
    if (atomStyle === "charge") {
      const sym = symbolByType.get(a.type) ?? "";
      const q = charges?.get(sym) ?? 0;
      L.push(`${i + 1} ${a.type} ${q.toFixed(4)} ${coords}`);
    } else {
      L.push(`${i + 1} ${a.type} ${coords}`);
    }
  });
  L.push("");
  return L.join("\n");
}

// LJ cutoff in Å. UFF interactions are short-ranged; ~3·σ_max captures the
// well for the largest atoms (σ up to ~4.2 Å) without an over-long neighbor
// build. Clamped per-system below against the actual largest σ present.
const LJ_CUTOFF_FLOOR = 10.0;

/**
 * Render a LAMMPS input script that reads the data file and runs a
 * molecular-dynamics simulation of the hotspot.
 *
 * Physics choices, and why:
 *  • `units real` — UFF parameters are in kcal/mol (ε) and Å (σ), which is
 *    exactly LAMMPS `units real`, so the UFF numbers go in unconverted.
 *  • `dimension 3` — the run is full 3D for richer, better-looking dynamics.
 *    The 2D sandbox region is extruded into a solid 3D crystal slab (see
 *    SLAB_THICKNESS_FACTOR), so atoms move and tumble in all three axes.
 *  • `pair_style lj/cut` with explicit per-type `pair_coeff i i ε σ` from UFF,
 *    and `pair_modify mix geometric` for unlike pairs — UFF's own combining
 *    rule (σ_ij = √(σ_i·σ_j), ε_ij = √(ε_i·ε_j)).
 *  • `minimize` before dynamics — the lattice placement can seed close
 *    contacts; a short minimization relaxes them so the run doesn't blow up.
 *  • `fix nve` + `fix langevin` at the snapshot's mean temperature — a
 *    thermostatted run holding the system near the temperature the cells were
 *    actually at in the sandbox.
 *
 * Atomify reads the `#/atom` comment commands to colour atoms by element.
 */
export function writeInputScript(
  system: LammpsSystem,
  dataFileName: string,
): string {
  const L: string[] = [];
  L.push("# Atomify hotspot run — generated by RealChem Atomic Mode");
  L.push("# Interactions: Universal Force Field (Rappé et al. 1992) LJ terms.");
  // Atomify command: colour each atom type by its real element.
  for (const t of system.types) {
    L.push(`#/atom ${t.id} ${t.symbol}`);
  }
  L.push("");
  L.push("units real");
  L.push("dimension 3");
  L.push("boundary p p p");
  L.push("atom_style atomic");
  L.push("");
  L.push(`read_data ${dataFileName}`);
  L.push("");

  // Cutoff: at least the floor, and at least 3× the largest σ in play.
  const maxSigma = system.types.reduce((m, t) => Math.max(m, t.sigma), 0);
  const cutoff = Math.max(LJ_CUTOFF_FLOOR, 3 * maxSigma).toFixed(2);
  L.push(`pair_style lj/cut ${cutoff}`);
  // Per-type UFF ε (kcal/mol) and σ (Å). Diagonal terms only; unlike pairs
  // come from the geometric mixing rule below (UFF's own combining rule).
  for (const t of system.types) {
    L.push(
      `pair_coeff ${t.id} ${t.id} ${t.epsilon.toFixed(4)} ${t.sigma.toFixed(4)}` +
        ` # ${t.symbol}${t.uffKnown ? "" : " (UFF fallback)"}`,
    );
  }
  L.push("pair_modify mix geometric");
  L.push("");
  L.push("neighbor 2.0 bin");
  L.push("neigh_modify every 1 delay 0 check yes");
  L.push("");
  // Relax close contacts from lattice placement before dynamics.
  L.push("minimize 1.0e-4 1.0e-6 1000 10000");
  L.push("");
  const temp = Math.max(1, system.meanTempK).toFixed(2);
  L.push(`velocity all create ${temp} 4928459 mom yes rot yes dist gaussian`);
  L.push("");
  L.push("fix 1 all nve");
  // langevin damping period in fs (units real) — 100 fs is a gentle thermostat.
  L.push(`fix 2 all langevin ${temp} ${temp} 100.0 904297`);
  L.push("");
  // Measurements: diffusion (MSD), structure (RDF), thermodynamics.
  L.push("compute msd all msd");
  L.push("compute rdf all rdf 100");
  L.push("fix msdlog all ave/time 10 1 10 c_msd[4] file msd.txt mode scalar");
  L.push("thermo_style custom step temp pe ke etotal press vol c_msd[4]");
  for (const line of measurementVariableLines()) L.push(line);
  L.push("");
  // 1 fs timestep (units real) — stable for LJ at these temperatures.
  L.push("timestep 1.0");
  L.push("thermo 50");
  L.push("run 5000");
  L.push("");
  return L.join("\n");
}
